<template>
  <Loader v-if="!isLoaded"/>
  <div v-else>
    <div class="with-side-panel">
      <div class="content-i">
        <div class="content-box">
          <div class="element-wrapper">

            <BackButton
                :label="$t('general.button.back')"
                :to="{ name: 'userList' }"
                marginBottom="5"/>

            <div class="d-flex justify-content-between">
              <h6 class="element-header">
                  {{ $t('user.title') }}
                  <span v-if="userId"> -
                  {{ userData?.firstName }}
                  {{ userData?.lastName }}
                  ({{ userData?.engagementTypeTranslation }})
                </span>
              </h6>
            </div>

            <div class="element-box">
              <UserMenu :userId="userId" :employeeType="employeeType"/>

              <form v-if="isLoaded" @submit.prevent="submitForm" novalidate>
                <div v-if="userId" class="row mt-5">
                  <div class="col-12 user-audit-info">
                    <AuditInfo
                        :user-role="currentUserRole"
                        :created-by-user-data="userData?.createdByUserData"
                        :created-at="userData?.createdAt"
                        :updated-by-user-data="userData?.updatedByUserData"
                        :updated-at="userData?.updatedAt"/>
                  </div>
                </div>
                <div class="row mb-5" :class="{ 'mt-5': !userId }">
                  <div class="col-12 d-flex justify-content-end">
                    <SaveButton
                        :label="$t('general.button.save')"
                        :loading="isSubmitting"/>

                    <CreateButton
                        v-if="userId"
                        class="ms-3 password-button"
                        label="Passwort ändern"
                        :to="{ name: 'changePasswordForUser', params: { id: userId } }">
                    </CreateButton>
                  </div>
                </div>

                <ValidationErrorMessage :validationErrors=v$?.$errors></ValidationErrorMessage>

                <div class="row">
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <BaseInput
                        v-model="formData.employeeNumber"
                        :label="$t('general.employee_number')"
                        :required=true
                        :validation="v$?.employeeNumber"
                        :messages="v$?.employeeNumber?.required?.$message"
                        readonly/>
                  </div>
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <div v-if="engagementTypeDropdown && engagementTypeDropdown.length">
                      <SelectVue
                          v-model="formData.engagementType"
                          :options="engagementTypeDropdown"
                          @update:modelValue="changeEngagementType"
                          :required="true"
                          :label="$t('general.user_engagement_type')"
                          disabled="disabled"
                          :validation="v$?.engagementType"
                          :messages="v$?.engagementType?.required?.$message"/>
                    </div>
                  </div>
                  <div class="col-lg-4 col-md-6 col-sm-12 text-center">
                    <!-- BSG external user: with contract (set password link by email) or without (password below) -->
                    <BaseToggle
                        v-if="isBsgExternalOnboarding"
                        v-model="formData.withContract"
                        @update:modelValue="onChangeWithContract"
                        label="Mit Vertrag"/>
                  </div>
                </div>

                <div class="row">
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <BaseInput
                        v-model="formData.firstName"
                        :label="$t('general.first_name')"
                        :required=true
                        :disabled="disableInternalFields"
                        :validation="v$?.firstName"
                        :messages="v$.firstName?.required?.$message"/>
                  </div>
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <BaseInput
                        v-model="formData.lastName"
                        :label="$t('general.last_name')"
                        :required=true
                        :disabled="disableInternalFields"
                        :validation="v$?.lastName"
                        :messages="v$?.lastName?.required?.$message"/>
                  </div>
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <!-- BSG external user with contract gets a "set password" link by email instead -->
                    <BaseInput
                        v-if="!userId && !isBsgContractOnboarding" v-model="formData.password"
                        type="password"
                        label="Password"
                        :required=true
                        :disabled="disableInternalFields"
                        :validation="v$.password"
                        :messages="v$.password?.required?.$message"/>

                    <div v-if="isBsgContractOnboarding && userContractTypesDropdown.length">
                      <SelectVue
                          v-model="formData.contractType"
                          :options="userContractTypesDropdown"
                          :required="true"
                          label="Vertrag"
                          :validation="v$?.contractType"
                          :messages="v$?.contractType?.required?.$message"/>
                    </div>
                  </div>
                </div>

                <div class="row">
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <div v-if="accessRightsDropdown && accessRightsDropdown.length">
                      <SelectVue
                          v-model="formData.accessRight"
                          :options="accessRightsDropdown"
                          :is-clear="true"
                          :required="true"
                          :disabled="disableInternalFields"
                          :label="$t('general.access_right')"
                          :validation="v$?.accessRight"
                          :messages="v$?.accessRight?.required?.$message"/>
                    </div>
                  </div>
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <Datepicker
                        v-model="formData.terminationDay"
                        :disabled="disableInternalFields"
                        :label="$t('general.termination_day')"/>
                  </div>
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <BaseInput
                        v-model="formData.email"
                        :label="$t('general.email')"
                        :required=true
                        :disabled="disableInternalFields"
                        :validation="v$?.email"
                        :messages="v$?.email?.required.$invalid ? v$?.email?.required?.$message : v$?.email?.email?.$message"/>
                  </div>
                </div>

                <div v-if="isBsgContractOnboarding && userContractTypesDropdown.length"
                     class="alert alert-info mt-2">
                  Der Mitarbeiter erhält per E-Mail einen Link, um sein Passwort zu vergeben.
                  Bis alle Vertragsunterlagen unterschrieben sind, hat er nur Zugriff auf „Meine Verträge“.
                </div>

                <!-- INTERN FIELDS -->
                <div class="row mt-3 pt-3"
                     style=" border-top: 1px solid #D8D8D8"
                     v-if="
                         formData.engagementType === 'intern' ||
                         formData.engagementType === 'others'">
                  <div class="col-12"><h6 class="mb-3">Informationen für interne Mitarbeiter</h6></div>

                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <Datepicker
                        v-model="formData.startingDate"
                        :required="true"
                        :label="$t('general.starting_date')"
                        :disabled="disableInternalFields"
                        :validation="v$?.startingDate"
                        :messages="v$?.startingDate?.required?.$message"/>

                    <BaseNumber
                        v-model="formData.workingHours"
                        :label="$t('general.working_hours')"
                        :required=true
                        :disabled="disableInternalFields"
                        :validation="v$?.workingHours"
                        :messages="v$?.workingHours?.required?.$message"/>
                  </div>

                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <div v-if="internCoordinatorDropdown && internCoordinatorDropdown.length">
                      <SelectVue
                          v-model="formData.internalCoordinatorId"
                          :options="internCoordinatorDropdown"
                          :is-clear="true"
                          :required="true"
                          :disabled="disableInternalFields"
                          :label="$t('general.responsible_site_manager')"
                          :validation="v$?.internalCoordinatorId"
                          :messages="v$?.internalCoordinatorId?.required?.$message"
                          position-type="top"/>

                      Arbeitstage: <br>
                      <span v-for="(workingDay, index) in formData.workingDays" :key="index">
                        - {{ $t('general.weekDays.' + workingDay) }}<br>
                      </span>
                    </div>
                  </div>
                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <div v-if="provinceDropdown && provinceDropdown.length">
                      <SelectVue
                          v-model="formData.provinceId"
                          :options="provinceDropdown"
                          :is-clear="true"
                          :required="true"
                          :disabled="disableInternalFields"
                          :label="$t('general.relevant_calendar_holidays')"
                          :validation="v$?.provinceId"
                          :messages="v$?.provinceId?.required?.$message"
                          position-type="top"/>
                    </div>
                  </div>
                </div>


                <!-- EXTERN FIELDS -->
                <div class="row mt-3 pt-3"
                     style=" border-top: 1px solid #D8D8D8"
                     v-if="formData.engagementType === 'extern'">
                  <div class="col-12"><h6 class="mb-3">Information für externe Mitarbeiter</h6></div>

                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <SelectVue
                        v-model="formData.employeeType"
                        :options="employeeTypesDropdown"
                        :is-clear="true"
                        :required="true"
                        :label="$t('general.employment_type')"
                        :validation="v$?.employeeType"
                        :messages="v$?.employeeType?.required?.$message"/>
                    <BaseInput
                        v-model="formData.address"
                        :label="$t('general.street')"
                        :required=true
                        :validation="v$?.address"
                        :messages="v$?.address?.required?.$message"/>
                    <BaseInput
                        v-model="formData.zip"
                        :label="$t('general.postal_code')"
                        :required=true
                        :validation="v$?.zip"
                        :messages="v$?.zip?.required?.$message"/>
                    <BaseInput
                        v-model="formData.city"
                        :label="$t('general.city')"
                        :required=true
                        :validation="v$?.city"
                        :messages="v$?.city?.required?.$message"/>

                    <Datepicker
                        v-model="formData.birthdate"
                        :required="true"
                        :label="$t('general.birthdate')"
                        :validation="v$?.birthdate"
                        :messages="v$?.birthdate?.required?.$message"/>

                    <div v-if="genderDropdown && genderDropdown.length">
                      <SelectVue
                          v-model="formData.gender"
                          :options="genderDropdown"
                          :is-clear="true"
                          :required="true"
                          :label="$t('general.gender')"
                          :validation="v$?.gender"
                          :messages="v$?.gender?.required?.$message"/>
                    </div>

                    <BaseInput
                        v-model="formData.creditor"
                        label="Kreditor"/>
                  </div>

                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <div v-if="externCoordinatorDropdown && externCoordinatorDropdown.length">
                      <SelectVue
                          v-model="formData.externalCoordinatorId"
                          :options="externCoordinatorDropdown"
                          :is-clear="true"
                          :label="$t('general.extern_coordinator')"
                          :validation="v$?.externalCoordinatorId"
                          :messages="v$?.externalCoordinatorId?.required?.$message"
                          position-type="top"/>
                    </div>

                    <BaseInput
                        v-model="formData.phone"
                        :label="$t('general.phone_number')"/>
                    <BaseInput
                        v-model="formData.practiceName"
                        :label="$t('general.practice_name')"/>
                    <BaseInput
                        v-model="formData.taxNumber"
                        :label="$t('general.tax_number')"/>
                    <BaseInput
                        v-model="formData.iban"
                        :label="$t('general.iban')"/>
                    <BaseInput
                        v-model="formData.accountOwner"
                        :label="$t('general.account_owner')"/>
                  </div>

                  <div class="col-lg-4 col-md-6 col-sm-12">
                    <BaseNumber
                        v-model="formData.privateCoachingBasicPrice"
                        :isCurrency=true
                        :decimalPlaces="2"
                        label="Einzelcoaching (16k)"/>
                    <BaseNumber
                        v-model="formData.privateCoachingPremiumPrice"
                        :isCurrency=true
                        :decimalPlaces="2"
                        label="Einzelcoaching (MIC, GoArbeit, KOMPASS"/>
                    <BaseNumber
                        v-model="formData.caseManagementCoachingPrice"
                        :isCurrency=true
                        :decimalPlaces="2"
                        :label="$t('general.case_management_coaching_price')"/>
                    <BaseNumber
                        v-model="formData.assistedLivingCoachingPrice"
                        :isCurrency=true
                        :decimalPlaces="2"
                        :label="$t('general.assisted_living_coaching_price')"/>
                    <BaseInput
                        v-model="formData.bic"
                        :label="$t('general.bic')"/>
                  </div>
                </div>

                <div class="row mt-3 pt-3"
                     style="
                          border-top: 1px solid #D8D8D8;
                          margin-bottom: 250px;">

                  <div class="col-12">
                    <div v-if="officeDropdown && officeDropdown.length">
                      <SelectVue
                          v-model="formData.officeIds"
                          :options="officeDropdown"
                          :is-clear="true"
                          :is-multi="true"
                          :required="true"
                          :label="$t('general.locations')"
                          :validation="v$?.officeIds"
                          :messages="v$?.officeIds?.required?.$message"/>
                    </div>
                  </div>

                  <!-- BSG: offices that don't exist yet, created with status "In Prüfung" -->
                  <div v-if="isBsgContractOnboarding && userContractTypesDropdown.length" class="col-lg-6 col-md-12 mt-2">
                    <label class="w-100">Neue Standorte</label>
                    <div class="d-flex">
                      <input
                          v-model="newOfficeName"
                          type="text"
                          class="form-control"
                          placeholder="Name des neuen Standorts"
                          @keydown.enter.prevent="addNewOfficeName"/>
                      <button type="button" class="btn btn-primary ms-2" @click="addNewOfficeName">
                        Hinzufügen
                      </button>
                    </div>
                    <small class="text-muted">
                      Neue Standorte werden mit dem Status „In Prüfung“ angelegt und das QM-Team wird informiert.
                    </small>
                    <div class="mt-2">
                      <span v-for="(name, index) in formData.newOfficeNames"
                            :key="name"
                            class="badge text-bg-primary me-2 mb-1 px-2 py-1">
                        {{ name }}
                        <i class="fa-solid fa-xmark ms-1" style="cursor: pointer"
                           @click="removeNewOfficeName(index)"></i>
                      </span>
                    </div>
                  </div>
                </div>
              </form>

            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import userService from '@/api/User';
import AuditInfo from '@/components/AuditInfo.vue';
import BaseInput from '@/components/base/BaseInput.vue';
import BaseNumber from '@/components/base/BaseNumber.vue';
import BaseToggle from '@/components/base/BaseToggle.vue';
import CreateButton from '@/components/CreateButton.vue';
import Loader from '@/components/Loader.vue';
import Datepicker from '@/components/picker/Datepicker.vue';
import SelectVue from '@/components/SelectVue.vue';
import ValidationErrorMessage from '@/components/ValidationErrorMessage.vue';
import {ErrorToast, SuccessToast} from '@/components/VueToast';
import router from '@/router';
import {
  getExternalCoordinatorDropdown,
  getInternalCoordinatorDropdown,
  getOfficeDropdown,
  getProvinceDropdown
} from '@/shared/dropdowns';
import {emailRule, requiredRule} from '@/shared/validators/validationRules';
import {piniaEnumsStore} from '@/store/piniaEnumsStore';
import {piniaCompanySettingsStore} from '@/store/piniaCompanySettingsStore';
import {piniaUserStore} from '@/store/piniaUserStore';
import UserMenu from '@/views/App/User/UserMenu.vue';
import useVuelidate from '@vuelidate/core';
import {computed, onMounted, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {useRoute} from 'vue-router';
import BackButton from "@/components/button/BackButton.vue";
import SaveButton from "@/components/button/SaveButton.vue";

const {t} = useI18n();
const piniaEnumsState = piniaEnumsStore();
const getEnums = computed(() => piniaEnumsState.enums);
const piniaUserState = piniaUserStore();
const currentUserRole = computed(() => piniaUserState.currentUser?.user?.roles?.[0]);
const genderDropdown = ref([]);
const employeeTypesDropdown = ref([]);
const accessRightsDropdown = ref([]);
const officeDropdown = ref([]);
const engagementTypeDropdown = ref([]);
const provinceDropdown = ref([]);
const internCoordinatorDropdown = ref([]);
const externCoordinatorDropdown = ref([]);
const isSubmitting = ref(false);
const isLoaded = ref(false);
const route = useRoute();
const userId = route.params.id;
const userData = ref(null);
const newContractData = ref(null);
const disableContractFields = ref(false);
const employeeType = ref("")
const disableInternalFields = ref(false);
// computed, so the dropdown fills in also when the enums arrive after the form is mounted
const userContractTypesDropdown = computed(() => getEnums.value?.userContractTypes || []);
const newOfficeName = ref('');

const piniaCompanySettingsState = piniaCompanySettingsStore();

const {daysInWeek} = computed(() => piniaEnumsState.enums).value;

const formData = ref({
  // Common fields
  id: null,
  employeeNumber: null, // required
  firstName: null, // required
  lastName: null, // required
  email: null, // required
  password: null, // required
  accessRight: null, // required
  officeIds: [], // required
  engagementType: 'extern', // required
  terminationDay: null,

  // Intern fields
  startingDate: null, // required
  workingHours: null, // required
  provinceId: null, // required
  internalCoordinatorId: null,
  partTime: false,
  workingDays: [],

  // Extern fields
  address: null,  // required
  zip: null, // required
  city: null, // required
  birthdate: null, // required
  gender: null, // required
  phone: null,
  creditor: null,
  practiceName: null,
  taxNumber: null,
  accountOwner: null,
  iban: null,
  bic: null,
  privateCoachingBasicPrice: null,
  privateCoachingPremiumPrice: null,
  caseManagementCoachingPrice: null,
  assistedLivingCoachingPrice: null,
  externalCoordinatorId: null,

  // BSG external user onboarding (create only)
  withContract: true,
  contractType: null,
  newOfficeNames: [],
  employeeType: null,


  // maritalStatus: null,
  // religion: null,
  // taxOffice: null, // required
  // taxIdNumber: null, // required
  // taxBracket: null, // required
  // firstAidTraining: null,
  // workingMinutes: null, // required
  // pay: null, // required
  // holidays: null,  // required
  // healthInsurance: null,
  // insuranceNumber: null, // required
  // pensionInsuranceNumber: null, // required
  // fixedTermEmploymentContract: null,
  // fireProtectionTraining: null,
  // personalRecordsComplete: '0',
  // contractDocumentsComplete: '0',
  // startNewContract: false,
  // newContractStartDate: '',

});

const rules = ref({
  // Common fields
  employeeNumber: requiredRule(t),
  firstName: requiredRule(t),
  lastName: requiredRule(t),
  email: emailRule(t),
  password: requiredRule(t),
  accessRight: requiredRule(t),
  officeIds: requiredRule(t),
  engagementType: requiredRule(t),

  // Intern user fields
  startingDate: requiredRule(t),
  workingHours: requiredRule(t),
  provinceId: requiredRule(t),

  // Extern
  address: requiredRule(t),
  zip: requiredRule(t),
  city: requiredRule(t),
  birthdate: requiredRule(t),
  gender: requiredRule(t),
  employeeType: requiredRule(t),

  // externalCoordinatorId: '',
  // taxOffice: requiredRule(t),
  // taxIdNumber: requiredRule(t),
  // taxBracket: requiredRule(t),
  // workingMinutes: requiredRule(t),
  // pay: requiredRule(t),
  // holidays: requiredRule(t),
  // insuranceNumber: requiredRule(t),
  // pensionInsuranceNumber: requiredRule(t),

});

// BSG: new external user can get new offices and is created with or without contract.
// Switched on together with the office approval workflow (backend env), otherwise the form is the same as without contract
const isBsgExternalOnboarding = computed(() =>
    !userId
    && !!piniaCompanySettingsState.companySettings?.officeApprovalWorkflowEnabled
    && formData.value.engagementType === 'extern'
);

// with contract: "set password" link instead of password, contract type required
const isBsgContractOnboarding = computed(() => isBsgExternalOnboarding.value && formData.value.withContract);

const computedRules = computed(() => {
  if (!isBsgExternalOnboarding.value) {
    return rules.value;
  }

  return {
    ...rules.value,
    password: isBsgContractOnboarding.value ? '' : requiredRule(t),
    contractType: isBsgContractOnboarding.value ? requiredRule(t) : '',
    // new offices typed by name are enough, no existing office has to be selected
    officeIds: formData.value.newOfficeNames.length ? '' : requiredRule(t),
  };
});

function onChangeWithContract(withContract) {
  if (withContract) {
    formData.value.password = null;
  } else {
    formData.value.contractType = null;
    formData.value.newOfficeNames = [];
  }
}

function addNewOfficeName() {
  const name = newOfficeName.value.trim();
  if (name && !formData.value.newOfficeNames.includes(name)) {
    formData.value.newOfficeNames.push(name);
  }
  newOfficeName.value = '';
}

function removeNewOfficeName(index) {
  formData.value.newOfficeNames.splice(index, 1);
}

const v$ = useVuelidate(computedRules, formData);

async function submitForm() {
  v$.value.$touch();
  if (v$.value.$invalid) {
    return;
  }

  if (isSubmitting.value) return;
  isSubmitting.value = true;

  try {

    if (userId) {
      await userService.putUser(formData.value, +userId);
    } else {
      // contract only for BSG external user
      await userService.postUser({
        ...formData.value,
        withContract: isBsgContractOnboarding.value,
        contractType: isBsgContractOnboarding.value ? formData.value.contractType : null,
        newOfficeNames: isBsgContractOnboarding.value ? formData.value.newOfficeNames : [],
      });
    }

    await router.push({name: 'userList'});
    isSubmitting.value = false;
    SuccessToast(t('general.toasts.save_success'));

  } catch (error) {
    await ErrorToast(error);
  } finally {
    isSubmitting.value = false;
  }
}

async function getUser() {
  try {
    const {data} = await userService.getUser(+userId);
    const {user, newContract} = data.data;

    userData.value = user;
    newContractData.value = newContract;
    employeeType.value = user.employeeType

    const accessRights = user.userAccessRights;
    const engagementType = user.engagementType;
    const internalCoordinatorId = user.internalCoordinator?.id;
    const externalCoordinatorId = user.externalCoordinator?.id;
    const personalRecordsComplete = String(user.personalRecordsComplete);
    const contractDocumentsComplete = String(user.contractDocumentsComplete);
    const locations = user.locations || [];
    const locationArray = locations.map(location => location?.id).filter(id => id != null);
    const provinceId = user?.province?.id;
    const partTime = user.partTime;
    const privateCoachingBasicPrice = user.privateCoachingBasicPrice;
    const privateCoachingPremiumPrice = user.individualCoaching;
    const caseManagementCoachingPrice = user.caseManagementCoachingPrice;
    const assistedLivingCoachingPrice = user.assistedLivingCoachingPrice;

    changeEngagementType(engagementType);
    onChangePartTimeOnLoad(partTime);

    if (engagementType === 'intern' || engagementType === 'others') {
      disableInternalFields.value = true;
    }

    formData.value = {
      ...user,
      officeIds: locationArray,
      accessRight: accessRights,
      externalCoordinatorId,
      internalCoordinatorId,
      personalRecordsComplete: personalRecordsComplete,
      contractDocumentsComplete: contractDocumentsComplete,
      provinceId: provinceId,
      privateCoachingBasicPrice: privateCoachingBasicPrice,
      privateCoachingPremiumPrice: privateCoachingPremiumPrice,
      caseManagementCoachingPrice: caseManagementCoachingPrice,
      assistedLivingCoachingPrice: assistedLivingCoachingPrice,
    };
  } catch (error) {
    await ErrorToast(error);
  }
}

async function getUserNextNumber() {
  try {
    const {data} = await userService.getUserNextNumber();
    formData.value.employeeNumber = data.data;
  } catch (error) {
    await ErrorToast(error);
  }
}

function changeEngagementType(engagementType) {

  console.log("ENGAGEMENT TYPE", engagementType);
  if (engagementType === 'extern') {
    rules.value = {
      ...rules.value,
      startingDate: '',
      workingHours: '',
      provinceId: '',
      password: !userId ? requiredRule(t) : '',

      // taxOffice: '',
      // taxIdNumber: '',
      // taxBracket: '',
      //
      // workingMinutes: '',
      // pay: '',
      // holidays: '',
      // insuranceNumber: '',
      // pensionInsuranceNumber: '',
      // //externalCoordinatorId: requiredRule(t),
      // newContractStartDate: '',
    };

    formData.value.maritalStatus = null;
    formData.value.religion = null;
    formData.value.startingDate = null;
    formData.value.taxOffice = null;
    formData.value.taxIdNumber = null;
    formData.value.taxBracket = null;
    formData.value.firstAidTraining = null;
    formData.value.workingHours = null;
    formData.value.workingMinutes = null;
    formData.value.pay = null;
    formData.value.holidays = null;
    formData.value.healthInsurance = null;
    formData.value.insuranceNumber = null;
    formData.value.pensionInsuranceNumber = null;
    formData.value.fixedTermEmploymentContract = null;
    formData.value.fireProtectionTraining = null;
    formData.value.contractDocumentsComplete = null;
    formData.value.provinceId = null;
    formData.value.internalCoordinatorId = null;
    formData.value.partTime = false;
    formData.value.workingDays = [];
  } else {
    rules.value = {
      ...rules.value,
      startingDate: requiredRule(t),
      workingHours: requiredRule(t),
      provinceId: requiredRule(t),
      password: !userId ? requiredRule(t) : '',
      externalCoordinatorId: '',
      employeeType: '',
      address: '',
      zip: '',
      city: '',
      birthdate: '',
      gender: ''
    };

    formData.value.practiceName = null;
    formData.value.taxNumber = null;
    formData.value.privateCoachingBasicPrice = null;
    formData.value.privateCoachingPremiumPrice = null;
    formData.value.caseManagementCoachingPrice = null;
    formData.value.assistedLivingCoachingPrice = null;
    formData.value.externalCoordinatorId = null;
    formData.value.creditor = null;
  }
}

function onChangePartTime(event) {
  const value = event.target.checked;
  if (value === true) {
    rules.value = {
      ...rules.value,
      workingDays: requiredRule(t),
    };
  } else {
    rules.value = {
      ...rules.value,
      workingDays: '',
    };

    formData.value.partTime = null;
    formData.value.workingDays = [];
  }
}

function onChangePartTimeOnLoad(value) {
  if (value === true) {
    rules.value = {
      ...rules.value,
      workingDays: requiredRule(t),
    };
  } else {
    rules.value = {
      ...rules.value,
      workingDays: '',
    };

    formData.value.partTime = null;
    formData.value.workingDays = [];
  }
}

function onChangeStartNewContract(event) {
  const value = event.target.checked;
  if (value === true) {
    disableContractFields.value = false;
    rules.value = {
      ...rules.value,
      newContractStartDate: requiredRule(t),
    };
  } else {
    disableContractFields.value = true;
    rules.value = {
      ...rules.value,
      newContractStartDate: '',
    };

    formData.value.newContractStartDate = '';
  }
}

async function getUserEnumDropdowns() {
  genderDropdown.value = getEnums.value?.genders || [];
  employeeTypesDropdown.value = getEnums.value?.employeeTypes || [];
  accessRightsDropdown.value = getEnums.value?.userAccessRights || [];
  engagementTypeDropdown.value = getEnums.value?.userEngagementTypes || [];
}

onMounted(async () => {
  //if (Object.keys(getEnums.value).length === 0) {
  //  await store.dispatch('getEnums', { t });
  //}

  changeEngagementType(formData.value.engagementType)

  // Enums are persisted in local storage, reload them if they are older than contract types
  if (!userId && !getEnums.value?.userContractTypes) {
    await piniaEnumsState.getEnums();
  }

  await Promise.all([
    getUserEnumDropdowns(),
    getOfficeDropdown(officeDropdown),
    getProvinceDropdown(provinceDropdown),
    getInternalCoordinatorDropdown(internCoordinatorDropdown),
    getExternalCoordinatorDropdown(externCoordinatorDropdown),
  ]);

  if (userId) {
    await getUser();
    disableContractFields.value = true;
  } else {
    await getUserNextNumber();
  }

  isLoaded.value = true;
});


</script>

<style scoped>

/* CreateButton wraps the link in two divs, stretch them so the button has the height of the save button */
.password-button,
.password-button > :deep(div) {
  display: flex;
}

/* Narrower audit info only on this form, full width on small screens */
@media (min-width: 992px) {
  .user-audit-info {
    max-width: 60%;
  }
}
</style>